import {
  createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  readAssistantPreferences,
  writeAssistantConversation,
  writeAssistantOpen,
} from '@/lib/ai-assistant/assistantPreferences';
import {
  OPEN_PAGE_TOOL,
  groupClientCalls,
  isEditorPath,
  type ClientCall,
  type ClientContext,
  type ClientResult,
  type PageBridge,
} from '@/lib/ai-assistant/clientTools';
import { resolveInternalPath } from '@/lib/ai-assistant/resolveInternalPath';
import { GENERATE_MY_AI_ASSISTANT_SUGGESTIONS, SUBMIT_AI_ASSISTANT_CLIENT_RESULTS } from '@/queries/aiAssistant';
import type { AssistantConversation } from '@/lib/ai-assistant/types';
import { createSuggestionCache } from '@/lib/ai-assistant/suggestionCache';
import { readEmbeddedApp } from '@/lib/app-embed/readEmbeddedApp';

// Panel state for the AI assistant sidebar. It lives above the routes (every
// page mounts its own Layout) so the open panel, the current conversation and
// the browser calls the assistant is running survive navigation, including
// navigation the assistant itself triggers.

const OPEN_PAGE_WAIT_MS = 20000;
const PAGE_SETTLE_MS = 600;

interface LastEdit {
  conversationId: string;
  bridgeKey: string;
  undo: () => void;
}

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  suggestionSession: number;
  pageBridgeKey: string | null;
  getSuggestions: (key: string, locale: string) => Promise<string[]>;
  /** Each page mounts its own Layout, which refetches myData. These remember
   *  the last answer for this account so the panel and its toggle stay on
   *  screen while the next page's request is in flight. */
  rememberAccess: (enabled: boolean) => void;
  rememberedAccess: () => boolean;
  /** The last conversation a chat loaded, for this account: a chat that
   *  remounts (page change, AI Studio) shows it at once and refreshes it. */
  cacheConversation: (conversation: AssistantConversation) => void;
  cachedConversation: (id: string | null) => AssistantConversation | null;
  // True while a Layout renders the panel (the user has the feature).
  panelMounted: boolean;
  setPanelMounted: (mounted: boolean) => void;
  /** True while AI Studio shows its own chat; the sidebar steps aside only
   *  then (a studio URL that is loading or failed still has the sidebar). */
  studioChatMounted: boolean;
  setStudioChatMounted: (mounted: boolean) => void;
  /** A page (the editor) registers what the assistant can see and do there. */
  registerPageBridge: (bridge: PageBridge) => () => void;
  /** Tools and page state sent with a message. */
  buildClientContext: () => ClientContext;
  /** This tab started the current turn of the conversation, so it runs the
   *  turn's browser calls (another tab or a reload must not run them twice). */
  markConversationOwned: (id: string) => void;
  ownsConversation: (id: string) => boolean;
  /** Runs the calls once (deduped by id) and posts the results. */
  runClientCalls: (conversationId: string, calls: ClientCall[]) => Promise<AssistantConversation | null>;
  lastEdit: { conversationId: string } | null;
  undoLastEdit: () => void;
  dismissLastEdit: () => void;
  /** The open page can capture what it shows (the editor preview). */
  frameCaptureAvailable: boolean;
  /** The current page frame, uploaded; null when it could not be captured. */
  captureCurrentFrame: () => Promise<{ url: string; name: string } | null>;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

const safeStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

// Caches above are per account: a different token never sees them.
const currentToken = (): string | null => {
  try {
    return safeStorage()?.getItem('token') ?? null;
  } catch {
    return null;
  }
};

const MAX_CACHED_CONVERSATIONS = 4;

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

export function AssistantProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  const [initial] = useState(() => readAssistantPreferences(safeStorage()));
  const [open, setOpenState] = useState(initial.open);
  const [conversationId, setConversationIdState] = useState<string | null>(initial.conversationId);
  const [suggestionSession, setSuggestionSession] = useState(0);
  const [pageBridgeKey, setPageBridgeKey] = useState<string | null>(null);
  const suggestionCacheRef = useRef<ReturnType<typeof createSuggestionCache> | null>(null);
  if (!suggestionCacheRef.current) suggestionCacheRef.current = createSuggestionCache();
  const suggestionTokenRef = useRef<string | null>(null);
  const [panelMounted, setPanelMounted] = useState(false);
  const [studioChatMounted, setStudioChatMounted] = useState(false);
  const [lastEdit, setLastEdit] = useState<LastEdit | null>(null);
  const lastEditRef = useRef<LastEdit | null>(null);
  lastEditRef.current = lastEdit;

  const bridgeRef = useRef<PageBridge | null>(null);
  const bridgePathRef = useRef<string | null>(null);
  const bridgeWaitersRef = useRef(new Set<() => void>());
  const handledCallsRef = useRef(new Set<string>());
  const ownedRef = useRef(new Set<string>());

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeAssistantOpen(safeStorage(), next);
  }, []);
  const toggle = useCallback(() => {
    setOpenState((current) => {
      writeAssistantOpen(safeStorage(), !current);
      return !current;
    });
  }, []);
  const setConversationId = useCallback((id: string | null) => {
    if (id === null) setSuggestionSession((current) => current + 1);
    setConversationIdState(id);
    writeAssistantConversation(safeStorage(), id);
  }, []);

  const [frameCaptureAvailable, setFrameCaptureAvailable] = useState(false);

  const registerPageBridge = useCallback((bridge: PageBridge) => {
    bridgeRef.current = bridge;
    bridgePathRef.current = pathRef.current;
    setPageBridgeKey(bridge.key);
    setFrameCaptureAvailable(!!bridge.captureFrame);
    bridgeWaitersRef.current.forEach((wake) => wake());
    // An undo belongs to the page state it was made on (e.g. that clip).
    if (lastEditRef.current && lastEditRef.current.bridgeKey !== bridge.key) setLastEdit(null);
    return () => {
      if (bridgeRef.current === bridge) {
        bridgeRef.current = null;
        setPageBridgeKey(null);
        setFrameCaptureAvailable(false);
      }
      if (lastEditRef.current?.bridgeKey === bridge.key) setLastEdit(null);
    };
  }, []);

  const captureCurrentFrame = useCallback(async () => {
    const capture = bridgeRef.current?.captureFrame;
    if (!capture) return null;
    try {
      return await capture();
    } catch {
      return null;
    }
  }, []);

  const buildClientContext = useCallback((): ClientContext => {
    const bridge = bridgePathRef.current === pathRef.current ? bridgeRef.current : null;
    let page: Record<string, unknown> | null = null;
    let tools = [OPEN_PAGE_TOOL];
    if (bridge) {
      try {
        page = bridge.getState();
        tools = [OPEN_PAGE_TOOL, ...bridge.getTools()];
      } catch (error) {
        console.error('[assistant] page bridge failed', error);
      }
    }
    const surface = readEmbeddedApp(safeStorage());
    return { path: pathRef.current, tools, page, ...(surface ? { surface } : {}) };
  }, []);

  const accessRef = useRef<{ token: string | null; enabled: boolean } | null>(null);
  const rememberAccess = useCallback((enabled: boolean) => {
    accessRef.current = { token: currentToken(), enabled };
  }, []);
  const rememberedAccess = useCallback(() => {
    const remembered = accessRef.current;
    return !!remembered && remembered.enabled && remembered.token === currentToken();
  }, []);

  // By id: a late reply for a conversation the chat already left must not
  // evict the one on screen.
  const conversationCacheRef = useRef(new Map<string, { token: string | null; conversation: AssistantConversation }>());
  const cacheConversation = useCallback((conversation: AssistantConversation) => {
    const cache = conversationCacheRef.current;
    cache.delete(conversation.id);
    cache.set(conversation.id, { token: currentToken(), conversation });
    if (cache.size > MAX_CACHED_CONVERSATIONS) cache.delete(cache.keys().next().value!);
  }, []);
  const cachedConversation = useCallback((id: string | null) => {
    const cached = id ? conversationCacheRef.current.get(id) : undefined;
    return cached && cached.token === currentToken() ? cached.conversation : null;
  }, []);

  const getSuggestions = useCallback((key: string, locale: string) => {
    const token = currentToken();
    if (token !== suggestionTokenRef.current) {
      suggestionTokenRef.current = token;
      suggestionCacheRef.current = createSuggestionCache();
    }
    const clientContext = buildClientContext();
    return suggestionCacheRef.current!.get(key, async (previous) => {
      const { data } = await client.mutate<{ generateMyAiAssistantSuggestions: string[] }>({
        mutation: GENERATE_MY_AI_ASSISTANT_SUGGESTIONS,
        variables: {
          sessionId: crypto.randomUUID(),
          path: clientContext.path,
          clientContext,
          locale,
          previous,
        },
      });
      return data?.generateMyAiAssistantSuggestions ?? [];
    });
  }, [client, buildClientContext]);

  const waitForBridge = useCallback(async (kind: PageBridge['kind'], timeoutMs: number) => {
    if (bridgeRef.current?.kind === kind) return true;
    return new Promise<boolean>((resolve) => {
      const wake = () => {
        if (bridgeRef.current?.kind !== kind) return;
        bridgeWaitersRef.current.delete(wake);
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        bridgeWaitersRef.current.delete(wake);
        resolve(false);
      }, timeoutMs);
      bridgeWaitersRef.current.add(wake);
    });
  }, []);

  const openPage = useCallback(async (call: ClientCall): Promise<ClientResult> => {
    const path = resolveInternalPath(call.args?.path, [window.location.origin, 'https://app.autoclipper.live']);
    if (!path) return { id: call.id, ok: false, error: 'invalid_path' };
    const target = path.split(/[?#]/, 1)[0];
    if (target !== pathRef.current) {
      // The old page's bridge goes away with it; wait for the new one. Its own
      // unregister will no longer find it current, so clear what it offered.
      if (bridgeRef.current) {
        bridgeRef.current = null;
        setPageBridgeKey(null);
        setFrameCaptureAvailable(false);
      }
      navigate(path);
    }
    if (isEditorPath(target)) {
      const ready = await waitForBridge('editor', OPEN_PAGE_WAIT_MS);
      return ready
        ? { id: call.id, ok: true, result: { opened: target, editor_ready: true } }
        : { id: call.id, ok: false, error: 'editor_not_ready', message: 'The editor did not finish loading' };
    }
    await sleep(PAGE_SETTLE_MS);
    return { id: call.id, ok: true, result: { opened: target } };
  }, [navigate, waitForBridge]);

  const runClientCalls = useCallback(async (
    targetConversationId: string,
    calls: ClientCall[],
  ): Promise<AssistantConversation | null> => {
    const fresh = calls.filter((call) => !handledCallsRef.current.has(call.id));
    if (!fresh.length) return null;
    fresh.forEach((call) => handledCallsRef.current.add(call.id));

    const results: ClientResult[] = [];
     
    for (const group of groupClientCalls(fresh)) {
      if (group.handler === 'open_page') {
         
        results.push(await openPage(group.calls[0]));
      } else if (group.handler === 'editor') {
        const bridge = bridgeRef.current;
        if (!bridge || bridge.kind !== 'editor') {
          group.calls.forEach((call) => results.push({
            id: call.id,
            ok: false,
            error: 'editor_not_open',
            message: 'Open the clip editor first with ui_open_page',
          }));
        } else {
          try {
             
            const outcome = await bridge.execute(group.calls);
            results.push(...outcome.results);
            // A batch that ran while the user switched clips (or left) must
            // not offer an undo for the page that is open now.
            if (outcome.undo && bridgeRef.current === bridge) {
              setLastEdit({ conversationId: targetConversationId, bridgeKey: bridge.key, undo: outcome.undo });
            }
          } catch (error) {
            console.error('[assistant] editor calls failed', error);
            group.calls.forEach((call) => results.push({ id: call.id, ok: false, error: 'editor_failed' }));
          }
        }
      } else {
        group.calls.forEach((call) => results.push({ id: call.id, ok: false, error: 'tool_unavailable' }));
      }
    }

    // Let React commit the edits so the page state sent back is the new one.
    await sleep(120);
    const { data } = await client.mutate<{ submitAiAssistantClientResults: AssistantConversation }>({
      mutation: SUBMIT_AI_ASSISTANT_CLIENT_RESULTS,
      variables: { conversationId: targetConversationId, results, clientContext: buildClientContext() },
    });
    return data?.submitAiAssistantClientResults ?? null;
  }, [client, openPage, buildClientContext]);

  const markConversationOwned = useCallback((id: string) => {
    ownedRef.current.add(id);
  }, []);
  const ownsConversation = useCallback((id: string) => ownedRef.current.has(id), []);

  const undoLastEdit = useCallback(() => {
    const edit = lastEditRef.current;
    if (!edit) return;
    setLastEdit(null);
    edit.undo();
  }, []);
  const dismissLastEdit = useCallback(() => setLastEdit(null), []);

  const value = useMemo(() => ({
    open,
    setOpen,
    toggle,
    conversationId,
    setConversationId,
    suggestionSession,
    pageBridgeKey: bridgePathRef.current === location.pathname ? pageBridgeKey : null,
    getSuggestions,
    rememberAccess,
    rememberedAccess,
    cacheConversation,
    cachedConversation,
    panelMounted,
    setPanelMounted,
    studioChatMounted,
    setStudioChatMounted,
    registerPageBridge,
    buildClientContext,
    markConversationOwned,
    ownsConversation,
    runClientCalls,
    lastEdit: lastEdit ? { conversationId: lastEdit.conversationId } : null,
    undoLastEdit,
    dismissLastEdit,
    frameCaptureAvailable,
    captureCurrentFrame,
  }), [
    open, setOpen, toggle, conversationId, setConversationId, suggestionSession, pageBridgeKey,
    getSuggestions, rememberAccess, rememberedAccess, cacheConversation, cachedConversation,
    panelMounted, studioChatMounted, location.pathname,
    registerPageBridge,
    buildClientContext, markConversationOwned, ownsConversation, runClientCalls, lastEdit,
    undoLastEdit, dismissLastEdit, frameCaptureAvailable, captureCurrentFrame,
  ]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

const NOOP_UNREGISTER = () => {};
const FALLBACK: AssistantContextValue = {
  open: false,
  setOpen: () => {},
  toggle: () => {},
  conversationId: null,
  setConversationId: () => {},
  suggestionSession: 0,
  pageBridgeKey: null,
  getSuggestions: async () => [],
  rememberAccess: () => {},
  rememberedAccess: () => false,
  cacheConversation: () => {},
  cachedConversation: () => null,
  panelMounted: false,
  setPanelMounted: () => {},
  studioChatMounted: false,
  setStudioChatMounted: () => {},
  registerPageBridge: () => NOOP_UNREGISTER,
  buildClientContext: () => ({ path: '/', tools: [], page: null }),
  markConversationOwned: () => {},
  ownsConversation: () => false,
  runClientCalls: async () => null,
  lastEdit: null,
  undoLastEdit: () => {},
  dismissLastEdit: () => {},
  frameCaptureAvailable: false,
  captureCurrentFrame: async () => null,
};

// No-op outside the provider (tests, isolated renders) rather than throwing.
export function useAssistantPanel(): AssistantContextValue {
  return useContext(AssistantContext) ?? FALLBACK;
}
